import React, { useEffect, useState, Component } from 'react';
import { Users, Building2, Calendar, TrendingUp, CheckCircle, XCircle, Eye, Edit, Trash2, Download, RefreshCw, Search, Filter, ArrowUpDown } from 'lucide-react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Button } from '../../components/Button';
import { Brand, Organizer } from '../../services/dataService';
import { Match } from '../../services/matchingService';
interface User {
  id: string;
  email: string;
  role: 'brand' | 'organizer';
  name: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
}
interface SystemStats {
  totalUsers: number;
  totalBrands: number;
  totalOrganizers: number;
  pendingApplications: number;
  activeMatches: number;
  totalRevenue: number;
}
export function SuperAdminDashboard() {
  const [activeTab, setActiveTab] = useState<'overview' | 'brands' | 'organizers' | 'matches' | 'analytics'>('overview');
  const [users, setUsers] = useState<User[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [organizers, setOrganizers] = useState<Organizer[]>([]);
  const [matches, setMatches] = useState<Match[]>([]);
  const [stats, setStats] = useState<SystemStats>({
    totalUsers: 0,
    totalBrands: 0,
    totalOrganizers: 0,
    pendingApplications: 0,
    activeMatches: 0,
    totalRevenue: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  useEffect(() => {
    loadDashboardData();
  }, []);
  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Load all data from localStorage
      const allBrands = JSON.parse(localStorage.getItem('brands') || '[]') as Brand[];
      const allOrganizers = JSON.parse(localStorage.getItem('organizers') || '[]') as Organizer[];
      const allMatches = JSON.parse(localStorage.getItem('matches') || '[]') as Match[];
      setBrands(allBrands);
      setOrganizers(allOrganizers);
      setMatches(allMatches);
      // Combine into users array
      const brandUsers: User[] = allBrands.map(b => ({
        id: b.id,
        email: b.email,
        role: 'brand' as const,
        name: b.companyName,
        status: 'approved' as const,
        createdAt: b.createdAt.toString()
      }));
      const organizerUsers: User[] = allOrganizers.map(o => ({
        id: o.id,
        email: o.email,
        role: 'organizer' as const,
        name: o.organizerName,
        status: 'approved' as const,
        createdAt: o.createdAt.toString()
      }));
      const allUsers = [...brandUsers, ...organizerUsers];
      setUsers(allUsers);
      // Calculate stats
      setStats({
        totalUsers: allUsers.length,
        totalBrands: brandUsers.length,
        totalOrganizers: organizerUsers.length,
        pendingApplications: allUsers.filter(u => u.status === 'pending').length,
        activeMatches: allMatches.filter(m => m.status === 'pending' || m.status === 'accepted').length,
        totalRevenue: Math.floor(Math.random() * 500000) + 100000
      });
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };
  const getBrandName = (brandId: string) => {
    const brand = brands.find(b => b.id === brandId);
    return brand?.companyName || 'Unknown Brand';
  };
  const getOrganizerName = (organizerId: string) => {
    const organizer = organizers.find(o => o.id === organizerId);
    return organizer?.organizerName || 'Unknown Organizer';
  };
  const handleDeleteUser = async (userId: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      setUsers(users.filter(u => u.id !== userId));
    }
  };
  const tabs = [{
    id: 'overview',
    label: 'Overview',
    icon: TrendingUp
  }, {
    id: 'brands',
    label: 'All Brands',
    icon: Building2
  }, {
    id: 'organizers',
    label: 'All Organizers',
    icon: Calendar
  }, {
    id: 'matches',
    label: 'All Matches',
    icon: CheckCircle
  }, {
    id: 'analytics',
    label: 'Analytics',
    icon: TrendingUp
  }];
  const filteredBrands = brands.filter(b => b.companyName.toLowerCase().includes(searchTerm.toLowerCase()) || b.email.toLowerCase().includes(searchTerm.toLowerCase()) || b.industry.toLowerCase().includes(searchTerm.toLowerCase()));
  const filteredOrganizers = organizers.filter(o => o.organizerName.toLowerCase().includes(searchTerm.toLowerCase()) || o.email.toLowerCase().includes(searchTerm.toLowerCase()) || o.eventType.toLowerCase().includes(searchTerm.toLowerCase()));
  const filteredMatches = matches.filter(m => {
    const brandName = getBrandName(m.brandId).toLowerCase();
    const organizerName = getOrganizerName(m.organizerId).toLowerCase();
    const search = searchTerm.toLowerCase();
    return brandName.includes(search) || organizerName.includes(search);
  });
  return <DashboardLayout data-id="element-1426">
      <div className="space-y-6" data-id="element-1427">
        {/* Header */}
        <div className="flex items-center justify-between" data-id="element-1428">
          <div data-id="element-1429">
            <h1 className="text-3xl font-bold text-gray-900" data-id="element-1430">
              Super Admin Dashboard
            </h1>
            <p className="text-gray-600 mt-1" data-id="element-1431">
              Complete platform control and oversight
            </p>
          </div>
          <div className="flex gap-3" data-id="element-1432">
            <Button onClick={loadDashboardData} variant="secondary" data-id="element-1433">
              <RefreshCw className="w-4 h-4 mr-2" data-id="element-1434" />
              Refresh Data
            </Button>
            <Button data-id="element-1435">
              <Download className="w-4 h-4 mr-2" data-id="element-1436" />
              Export Report
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200" data-id="element-1437">
          <nav className="flex space-x-8" data-id="element-1438">
            {tabs.map(tab => {
            const Icon = tab.icon;
            return <button key={tab.id} onClick={() => setActiveTab(tab.id as any)} className={`
                    flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors
                    ${activeTab === tab.id ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                  `} data-id="element-1439">
                  <Icon className="w-5 h-5 mr-2" data-id="element-1440" />
                  {tab.label}
                </button>;
          })}
          </nav>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && <div className="space-y-6" data-id="element-1441">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1442">
              <StatCard title="Total Users" value={stats.totalUsers} icon={Users} color="blue" trend="+12% from last month" data-id="element-1443" />
              <StatCard title="Total Brands" value={stats.totalBrands} icon={Building2} color="purple" trend="+8% from last month" data-id="element-1444" />
              <StatCard title="Total Organizers" value={stats.totalOrganizers} icon={Calendar} color="green" trend="+15% from last month" data-id="element-1445" />
              <StatCard title="Pending Applications" value={stats.pendingApplications} icon={CheckCircle} color="yellow" data-id="element-1446" />
              <StatCard title="Active Matches" value={stats.activeMatches} icon={TrendingUp} color="indigo" trend="+23% from last month" data-id="element-1447" />
              <StatCard title="Platform Revenue" value={`$${stats.totalRevenue.toLocaleString()}`} icon={TrendingUp} color="green" trend="+18% from last month" data-id="element-1448" />
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow p-6" data-id="element-1449">
              <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1450">
                Quick Actions
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-1451">
                <Button variant="secondary" className="justify-center" onClick={() => setActiveTab('brands')} data-id="element-1452">
                  View All Brands
                </Button>
                <Button variant="secondary" className="justify-center" onClick={() => setActiveTab('organizers')} data-id="element-1453">
                  View All Organizers
                </Button>
                <Button variant="secondary" className="justify-center" onClick={() => setActiveTab('matches')} data-id="element-1454">
                  View All Matches
                </Button>
                <Button variant="secondary" className="justify-center" onClick={() => setActiveTab('analytics')} data-id="element-1455">
                  View Analytics
                </Button>
              </div>
            </div>
          </div>}

        {/* Brands Tab */}
        {activeTab === 'brands' && <div className="bg-white rounded-lg shadow overflow-hidden" data-id="element-1456">
            <div className="p-6 border-b border-gray-200" data-id="element-1457">
              <div className="flex items-center justify-between mb-4" data-id="element-1458">
                <div data-id="element-1459">
                  <h2 className="text-lg font-semibold text-gray-900" data-id="element-1460">
                    All Brands
                  </h2>
                  <p className="text-sm text-gray-600 mt-1" data-id="element-1461">
                    {filteredBrands.length} brands registered on the platform
                  </p>
                </div>
                <div className="relative" data-id="element-1462">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" data-id="element-1463" />
                  <input type="text" placeholder="Search brands..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-1464" />
                </div>
              </div>
            </div>

            {loading ? <div className="p-12 text-center text-gray-500" data-id="element-1465">
                Loading brands...
              </div> : <div className="overflow-x-auto" data-id="element-1466">
                <table className="w-full" data-id="element-1467">
                  <thead className="bg-gray-50" data-id="element-1468">
                    <tr data-id="element-1469">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1470">
                        Company
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1471">
                        Contact
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1472">
                        Industry
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1473">
                        Product
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1474">
                        Budget
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1475">
                        Joined
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-1476">
                    {filteredBrands.map(brand => <tr key={brand.id} className="hover:bg-gray-50" data-id="element-1477">
                        <td className="px-6 py-4" data-id="element-1478">
                          <div data-id="element-1479">
                            <div className="text-sm font-medium text-gray-900" data-id="element-1480">
                              {brand.companyName}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-1481">
                              {brand.email}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-1482">
                          <div className="text-sm text-gray-900" data-id="element-1483">
                            {brand.contactName}
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-1484">
                            {brand.phone}
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-1485">
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800" data-id="element-1486">
                            {brand.industry}
                          </span>
                        </td>
                        <td className="px-6 py-4" data-id="element-1487">
                          <div className="text-sm text-gray-900" data-id="element-1488">
                            {brand.productName}
                          </div>
                          <div className="text-sm text-gray-500 truncate max-w-xs" data-id="element-1489">
                            {brand.productDescription}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900" data-id="element-1490">
                          {brand.budget}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500" data-id="element-1491">
                          {new Date(brand.createdAt).toLocaleDateString()}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}
          </div>}

        {/* Organizers Tab */}
        {activeTab === 'organizers' && <div className="bg-white rounded-lg shadow overflow-hidden" data-id="element-1492">
            <div className="p-6 border-b border-gray-200" data-id="element-1493">
              <div className="flex items-center justify-between mb-4" data-id="element-1494">
                <div data-id="element-1495">
                  <h2 className="text-lg font-semibold text-gray-900" data-id="element-1496">
                    All Organizers
                  </h2>
                  <p className="text-sm text-gray-600 mt-1" data-id="element-1497">
                    {filteredOrganizers.length} organizers registered on the
                    platform
                  </p>
                </div>
                <div className="relative" data-id="element-1498">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" data-id="element-1499" />
                  <input type="text" placeholder="Search organizers..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-1500" />
                </div>
              </div>
            </div>

            {loading ? <div className="p-12 text-center text-gray-500" data-id="element-1501">
                Loading organizers...
              </div> : <div className="overflow-x-auto" data-id="element-1502">
                <table className="w-full" data-id="element-1503">
                  <thead className="bg-gray-50" data-id="element-1504">
                    <tr data-id="element-1505">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1506">
                        Organization
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1507">
                        Contact
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1508">
                        Event
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1509">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1510">
                        Attendees
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1511">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-1512">
                    {filteredOrganizers.map(organizer => <tr key={organizer.id} className="hover:bg-gray-50" data-id="element-1513">
                        <td className="px-6 py-4" data-id="element-1514">
                          <div data-id="element-1515">
                            <div className="text-sm font-medium text-gray-900" data-id="element-1516">
                              {organizer.organizerName}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-1517">
                              {organizer.email}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-1518">
                          <div className="text-sm text-gray-900" data-id="element-1519">
                            {organizer.contactName}
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-1520">
                            {organizer.phone}
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-1521">
                          <div className="text-sm font-medium text-gray-900" data-id="element-1522">
                            {organizer.eventName}
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-1523">
                            {organizer.location}
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-1524">
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800" data-id="element-1525">
                            {organizer.eventType}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900" data-id="element-1526">
                          {organizer.attendeeCount}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500" data-id="element-1527">
                          {organizer.eventDate}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}
          </div>}

        {/* Matches Tab */}
        {activeTab === 'matches' && <div className="bg-white rounded-lg shadow overflow-hidden" data-id="element-1528">
            <div className="p-6 border-b border-gray-200" data-id="element-1529">
              <div className="flex items-center justify-between mb-4" data-id="element-1530">
                <div data-id="element-1531">
                  <h2 className="text-lg font-semibold text-gray-900" data-id="element-1532">
                    All Matches
                  </h2>
                  <p className="text-sm text-gray-600 mt-1" data-id="element-1533">
                    {filteredMatches.length} matches created on the platform
                  </p>
                </div>
                <div className="relative" data-id="element-1534">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" data-id="element-1535" />
                  <input type="text" placeholder="Search matches..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-1536" />
                </div>
              </div>
            </div>

            {loading ? <div className="p-12 text-center text-gray-500" data-id="element-1537">
                Loading matches...
              </div> : <div className="overflow-x-auto" data-id="element-1538">
                <table className="w-full" data-id="element-1539">
                  <thead className="bg-gray-50" data-id="element-1540">
                    <tr data-id="element-1541">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1542">
                        Brand
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1543">
                        Organizer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1544">
                        Score
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1545">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1546">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1547">
                        Match Reasons
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-1548">
                    {filteredMatches.map(match => <tr key={match.id} className="hover:bg-gray-50" data-id="element-1549">
                        <td className="px-6 py-4" data-id="element-1550">
                          <div className="text-sm font-medium text-gray-900" data-id="element-1551">
                            {getBrandName(match.brandId)}
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-1552">
                          <div className="text-sm font-medium text-gray-900" data-id="element-1553">
                            {getOrganizerName(match.organizerId)}
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-1554">
                          <div className="flex items-center" data-id="element-1555">
                            <div className="text-sm font-bold text-gray-900 mr-2" data-id="element-1556">
                              {match.score}%
                            </div>
                            <div className="w-16 bg-gray-200 rounded-full h-2" data-id="element-1557">
                              <div className="bg-green-500 h-2 rounded-full" style={{
                        width: `${match.score}%`
                      }} data-id="element-1558" />
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-1559">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${match.status === 'accepted' ? 'bg-green-100 text-green-800' : match.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`} data-id="element-1560">
                            {match.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500" data-id="element-1561">
                          {new Date(match.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4" data-id="element-1562">
                          <ul className="text-xs text-gray-600 space-y-1" data-id="element-1563">
                            {match.matchReasons.slice(0, 3).map((reason, idx) => <li key={idx} data-id="element-1564">• {reason}</li>)}
                          </ul>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}
          </div>}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && <div className="space-y-6" data-id="element-1565">
            <div className="bg-white rounded-lg shadow p-6" data-id="element-1566">
              <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1567">
                Platform Analytics
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1568">
                <div data-id="element-1569">
                  <h3 className="text-sm font-medium text-gray-700 mb-2" data-id="element-1570">
                    Match Success Rate
                  </h3>
                  <div className="text-3xl font-bold text-gray-900" data-id="element-1571">
                    {matches.length > 0 ? Math.round(matches.filter(m => m.status === 'accepted').length / matches.length * 100) : 0}
                    %
                  </div>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-1572">
                    {matches.filter(m => m.status === 'accepted').length} of{' '}
                    {matches.length} matches accepted
                  </p>
                </div>
                <div data-id="element-1573">
                  <h3 className="text-sm font-medium text-gray-700 mb-2" data-id="element-1574">
                    Average Match Score
                  </h3>
                  <div className="text-3xl font-bold text-gray-900" data-id="element-1575">
                    {matches.length > 0 ? Math.round(matches.reduce((sum, m) => sum + m.score, 0) / matches.length) : 0}
                    %
                  </div>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-1576">
                    Across all matches
                  </p>
                </div>
              </div>
            </div>
          </div>}
      </div>
    </DashboardLayout>;
}
interface StatCardProps {
  title: string;
  value: string | number;
  icon: ComponentType<{
    className?: string;
  }>;
  color: 'blue' | 'purple' | 'green' | 'yellow' | 'indigo';
  trend?: string;
}
function StatCard({
  title,
  value,
  icon: Icon,
  color,
  trend
}: StatCardProps) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    purple: 'bg-purple-100 text-purple-600',
    green: 'bg-green-100 text-green-600',
    yellow: 'bg-yellow-100 text-yellow-600',
    indigo: 'bg-indigo-100 text-indigo-600'
  };
  return <div className="bg-white rounded-lg shadow p-6" data-id="element-1577">
      <div className="flex items-center justify-between mb-4" data-id="element-1578">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`} data-id="element-1579">
          <Icon className="w-6 h-6" data-id="element-1580" />
        </div>
      </div>
      <h3 className="text-sm font-medium text-gray-600 mb-1" data-id="element-1581">{title}</h3>
      <p className="text-3xl font-bold text-gray-900 mb-2" data-id="element-1582">{value}</p>
      {trend && <p className="text-sm text-green-600" data-id="element-1583">{trend}</p>}
    </div>;
}